import { useLocation } from 'react-router-dom'
import { Btn } from '../components/Btn'
import { BlogMetas } from '../components/BlogMetas'
import { InnerSidebar } from '../components/InnerSidebar'
import { useOptionalT } from '../i18n/LocaleProvider'
import { useSeo } from '../seo/useSeo'
import type { SeoSite } from '../seo/types'
import type { SocialLink } from '../components/types'
import { Markdown } from './Markdown'
import { blogSidebar } from './posts'
import { BLOG_AUTHOR, BLOG_SHARES, type BlogPost } from './types'

export interface BlogArticleProps {
  /** Every post this app carries — the sidebar is derived from all of them. */
  posts: BlogPost[]
  /** The one being read. `undefined` renders the not-found state. */
  post?: BlogPost
  /** The share row. Defaults to `BLOG_SHARES`, which is the same four on every site. */
  shares?: SocialLink[]
  /** Where "back to the blog" goes. Always `/blog` today; a prop so it cannot be assumed. */
  indexTo?: string
  /**
   * This app's site tree root, so a post that is **not** on this site can mark itself
   * `noindex`.
   *
   * A real post's head is written twice already — into per-route HTML by the build, and by
   * the site tree on navigation — and neither covers a slug that resolves to nothing. Left
   * alone, a Garage post's URL on the resort's host serves the resort's default head under
   * a page that says the post is elsewhere, and a crawler can index it.
   *
   * Optional, and `useSeo` takes `undefined` for exactly this reason: a hook some callers
   * invoke and others do not is not a hook.
   */
  site?: SeoSite
}

/**
 * One post, with the shared sidebar beside it.
 *
 * **Shared by all four public apps as of 2026-09-21**, out of `app_website`, for the reason
 * the index moved: one library on four sites means one thing that renders a post.
 *
 * **The article is its own layout, not a borrowed card.** It used to be `BlogCard`'s
 * `.ul-blog-2.ul-blog-inner`, which put the whole post inside a card's text column and
 * brought the card's furniture with it — a rule under the metas and a divider between them,
 * correct on a card and wrong on an article. `BlogMetas` is the one piece still shared with
 * the cards, because an author-and-date row genuinely is the same row.
 *
 * **No comments.** The page carried the template's thread — two identical lorem ipsum
 * comments, both from "Leslie Alexander", both dated March 20 2023, each with a Reply button
 * that did nothing — and a "Leave a Comment" form that was inert like every other form on
 * the platform. There is no comment backend and commenting is not a thing the Foundation has
 * decided to do, so the whole block is gone rather than hidden: a section nothing renders is
 * a section nobody can tell is deliberate. **Sharing stays** — it is the half a static build
 * can honestly offer, and the half that asks nothing of the Foundation.
 *
 * **No banner**, and the same two things travel with that as on the index: this component
 * carries `.ul-plain-page-top`, and the consuming app has to put `/blog/*` in its
 * `header.plainRoutes` — a prefix match, because no exact path covers a `:slug`.
 *
 * ## An unknown slug is a real case now, not a defensive branch
 *
 * While one app had the whole blog, a miss meant a typo and the page fell back to the first
 * post — which renders somebody else's article under the address you typed. Four apps now
 * carry *subsets* of one library, so the ordinary case is a link to a Garage post pasted
 * into the resort's site. The page says the post is not on this site and offers the index.
 */
export function BlogArticle({
  posts,
  post,
  shares = BLOG_SHARES,
  indexTo = '/blog',
  site,
}: BlogArticleProps) {
  const t = useOptionalT()
  const { pathname } = useLocation()

  // Called unconditionally and passed `undefined` for a post that exists — that page's head
  // is already written twice, by the build and by the site tree, and writing a third time
  // here would be this component overriding both.
  useSeo(post ? undefined : site, {
    title: t('ul.blog.notFound.title'),
    description: t('ul.blog.notFound.body'),
    path: pathname,
    noindex: true,
  })

  return (
    <div className="ul-section-spacing ul-plain-page-top">
      <div className="ul-container">
        <div className="row ul-bs-row gy-5 flex-column-reverse flex-md-row">
          {/* sidebar */}
          <div className="col-lg-3 col-md-4">
            <InnerSidebar {...blogSidebar(posts)} />
          </div>

          <div className="col-lg-9 col-md-8">
            {!post ? (
              <div className="ul-blog-details mb-0">
                <h1 className="ul-plain-page-title">{t('ul.blog.notFound.title')}</h1>
                <p className="ul-section-descr">{t('ul.blog.notFound.body')}</p>
                <div className="mt-4">
                  <Btn to={indexTo} icon="fast-forward-double-right-arrows-symbol">
                    {t('ul.blog.notFound.cta')}
                  </Btn>
                </div>
              </div>
            ) : (
              <div className="ul-blog-details mb-0">
                {/*
                  **One flow, with the photograph floated into it.** The picture is on the
                  left; the title, the date, the author, the tags and then the post all run
                  down its right-hand side and continue full width underneath it once they
                  pass its bottom edge.

                  That is a float and it has to be — a grid or a flex row cannot do it. Those
                  give the text a column, and a column beside a 360px picture leaves a column
                  of empty page under the picture for the length of the article, which is the
                  gap this replaced. A float shortens only the line boxes it is beside, so the
                  prose reclaims the width the moment the picture ends.

                  **Everything here is one sibling list on purpose**, in reading order, with
                  no wrapper around the title-and-metas group. A wrapper would be a block box
                  starting at the container's left edge, so the picture would sit on top of
                  it rather than beside it. `_blog-details.scss` carries the rest of the
                  reasoning, including which markdown blocks have to clear the float.
                */}
                <div className="ul-blog-details-article">
                  <div className="ul-blog-details-figure">
                    <img src={post.image} alt={post.imageAlt ?? t('ul.blogCard.imageAlt')} />
                  </div>

                  <h1 className="ul-blog-title">{post.title}</h1>

                  {/*
                    Title, then date, then author, then tags — four facts in that order and
                    nothing between them. The card's metas are `metasFor`, which pairs the
                    author with the post's category; the category is not one of the four and
                    tags already say what a post is about, so this page builds its own row
                    rather than appending to that one.
                  */}
                  <BlogMetas
                    metas={[
                      ...(post.published ? [{ icon: 'calendar', text: post.published }] : []),
                      { icon: 'account', text: BLOG_AUTHOR },
                    ]}
                  />

                  {post.tags.length > 0 && (
                    <div className="ul-blog-details-tags">
                      {/* A label, not a heading: it names the row beside it, and an <h4>
                          under the page's <h1> was a heading level nothing else used. */}
                      <span className="actions-title">{t('ul.blog.tags')}</span>
                      <div className="tags">
                        {post.tags.map((tag) => (
                          <a href="#" key={tag}>
                            {tag}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="ul-donation-details-summary-txt ul-blog-details-txt">
                    <Markdown>{post.body}</Markdown>
                  </div>
                </div>

                {/* The share row. The tags used to sit on its left — see the header above. */}
                <div className="ul-blog-details-actions">
                  <div className="shares-wrapper">
                    <span className="actions-title">{t('ul.blog.share')}</span>
                    <div className="share-options">
                      {shares.map((s) => (
                        <a key={s.icon} href={s.href} aria-label={s.label ?? s.icon}>
                          <i className={`flaticon-${s.icon}`} />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
